import { CheckOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Card, Drawer, Tag, Typography } from "antd";
import type { TableProps } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import { compareNumeric, compareText } from "../api/format";
import type {
  ListResponse,
  SpeakerSpeechStatisticRow,
  SpeechCongregationStatisticRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";

/**
 * Sidebar entry "speaker_speech_statistics" → /speaker/speech_statistics.
 *
 * The mirror image of the congregation statistics: which talks the speaker has
 * held and how often, drilling down into the congregations that heard each one.
 *
 * The list is not the speaker's selection — it is every talk they may be asked
 * for, plus any they have already given. `selected` marks the ones they
 * actually offer, which is what makes a row with assignments but no tick worth
 * looking at.
 */
export function SpeakerSpeechStatisticsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterValues>({});
  const [selected, setSelected] = useState<SpeakerSpeechStatisticRow | null>(null);

  const speeches = useQuery({
    queryKey: ["speaker", "speech_statistics", filters],
    queryFn: () =>
      apiFetch<ListResponse<SpeakerSpeechStatisticRow>>(
        withQuery("/speaker/speech_statistics", filters),
      ),
  });

  const congregations = useQuery({
    queryKey: ["speaker", "speech_statistics", "congregations", selected?.id, filters],
    queryFn: () =>
      apiFetch<ListResponse<SpeechCongregationStatisticRow>>(
        withQuery(`/speaker/speech_statistics/${selected!.id}/congregations`, {
          congregation_filter: filters.congregation_filter ?? "",
          circumscription_filter: filters.circumscription_filter ?? "",
        }),
      ),
    enabled: selected !== null,
  });

  const fields = useMemo(
    () => [
      { name: "speech_filter", label: t("activerecord.models.speech") },
      { name: "language_filter", label: t("activerecord.models.language") },
      { name: "congregation_filter", label: t("activerecord.models.congregation") },
      { name: "circumscription_filter", label: t("activerecord.models.circumscription") },
    ],
    [t],
  );

  const columns = useMemo<TableProps<SpeakerSpeechStatisticRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.speaker.assignations"),
        dataIndex: "assignation_count",
        width: 110,
        align: "right",
        defaultSortOrder: "descend",
        sorter: (a, b) => a.assignation_count - b.assignation_count,
        render: (value: number) => (value > 0 ? <Tag color="blue">{value}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "schema",
        width: 100,
        sorter: (a, b) => compareNumeric(a.schema, b.schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "title",
        ellipsis: true,
        sorter: (a, b) => compareText(a.title, b.title),
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 100,
        sorter: (a, b) => compareText(a.language_code, b.language_code),
      },
      {
        title: t("activerecord.attributes.speaker_speech.selected"),
        dataIndex: "selected",
        width: 110,
        align: "center",
        // A tick is nothing a spreadsheet can read; the export reads the raw
        // boolean and writes the localised "yes" for it.
        render: (value: boolean) => (value ? <CheckOutlined /> : null),
      },
    ],
    [t],
  );

  const congregationColumns = useMemo<TableProps<SpeechCongregationStatisticRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 170,
        defaultSortOrder: "descend",
        sorter: (a, b) => compareText(a.week_begin, b.week_begin),
      },
      {
        title: t("activerecord.attributes.circumscription.short"),
        dataIndex: "circumscription_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.congregation.congregation_code"),
        dataIndex: "congregation_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.congregation.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.congregation.contact_first_name"),
        dataIndex: "contact_first_name",
        width: 130,
      },
      {
        title: t("activerecord.attributes.congregation.contact_last_name"),
        dataIndex: "contact_last_name",
        width: 130,
      },
      { title: t("activerecord.attributes.congregation.phone"), dataIndex: "phone", width: 140 },
      { title: t("activerecord.attributes.congregation.city"), dataIndex: "city", width: 140 },
      { title: t("activerecord.attributes.congregation.country"), dataIndex: "country", width: 90 },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.speaker_speech_statistics")}>
      <FilterBar fields={fields} onApply={setFilters} loading={speeches.isFetching} />

      <DataTable
        columns={columns}
        rows={speeches.data?.data}
        loading={speeches.isPending}
        error={speeches.error}
        exportAs={t("navigation.speaker_speech_statistics")}
        onRowClick={setSelected}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        size={1040}
        destroyOnHidden
        title={
          selected && (
            <Typography.Text>
              {[selected.schema, selected.title].filter(Boolean).join(" – ")}
            </Typography.Text>
          )
        }
      >
        <Typography.Title level={5}>{t("navigation.manage_congregations")}</Typography.Title>

        {/* Named after the talk the congregations heard. */}
        <DataTable
          columns={congregationColumns}
          rows={congregations.data?.data}
          exportAs={[selected?.schema, t("navigation.manage_congregations")]
            .filter(Boolean)
            .join(" · ")}
          loading={congregations.isPending}
          error={congregations.error}
          paginated={false}
        />
      </Drawer>
    </Card>
  );
}
